import React from 'react'
import {
  Link as RouterLink,
  LinkProps as RouterLinkProps,
  NavLink as RouterNavLink,
  NavLinkProps as RouterNavLinkProps,
} from 'react-router'
import MuiLink, { LinkProps as MuiLinkProps } from '@mui/material/Link'
import { mixpanel } from '../../mixpanel'

const classes = {
  link: ({ underline }: { underline: MuiLinkProps['underline'] }) => ({
    textDecoration: underline === 'always' ? 'underline' : 'none',
    '&:hover': {
      color: 'inherit',
      textDecoration: underline === 'none' ? 'none' : 'underline',
    },
  }),
}

type MixpanelProps = {
  trackEvent?: Record<string, unknown> & { component: string }
}

type Props = {
  underline?: MuiLinkProps['underline']
  muiStyle?: MuiLinkProps['sx']
  available?: boolean
} & MixpanelProps

export interface LinkProps extends RouterLinkProps, Props {
  to: string
}

/**
 * Common link component
 * @param underline - when we should show the underline ('none' | 'hover' | 'always')
 * @param children - content of the link
 * @param muiStyle - css properties for the MaterialUI link component
 * @param rest - other properties (for Router link component)
 * @param available - whether we can edit the entity
 * @param to - url to link to
 * @param trackEvent - whether to track click events in mixpanel
 */
export const Link: React.FunctionComponent<LinkProps> = ({
  underline = 'none',
  children,
  muiStyle,
  style,
  available = true,
  to,
  trackEvent,
  ...rest
}) => {
  if (!available || to === '') return <span>{children}</span>

  const handleClick = () => {
    if (trackEvent) mixpanel.trackClickLink({ ...trackEvent, to })
  }

  return (
    <RouterLink
      data-testid={`routerlink-${to}`}
      {...rest}
      to={to}
      style={{ ...classes.link({ underline }), ...(style || {}) }}
    >
      <MuiLink underline={underline} component="span" sx={muiStyle} onClick={handleClick}>
        {children}
      </MuiLink>
    </RouterLink>
  )
}

/**
 * External link component
 */
export const ExternalLink: React.FunctionComponent<MuiLinkProps & MixpanelProps> = ({
  underline = 'none',
  trackEvent,
  children,
  ...rest
}) => {
  const handleClick: MuiLinkProps['onClick'] = (...args) => {
    if (trackEvent && rest.href) mixpanel.trackClickLink({ ...trackEvent, to: rest.href })
    rest.onClick?.(...args)
  }

  return (
    <MuiLink
      rel="noopener noreferrer"
      target="_blank"
      underline={underline}
      sx={classes.link({ underline })}
      onClick={handleClick}
      {...rest}
    >
      {children}
    </MuiLink>
  )
}

interface NavLinkProps extends RouterNavLinkProps, Props {
  to: string
  children: React.ReactNode
}
/**
 * Link for sidebar
 */
export const NavLink: React.FunctionComponent<NavLinkProps> = ({
  underline = 'none',
  children,
  muiStyle,
  trackEvent,
  ...rest
}) => {
  const handleClick = () => {
    if (trackEvent) mixpanel.trackClickLink({ ...trackEvent, to: rest.to })
  }

  return (
    <RouterNavLink {...rest} style={classes.link({ underline })}>
      <MuiLink underline={underline} component="span" sx={muiStyle} onClick={handleClick}>
        {children}
      </MuiLink>
    </RouterNavLink>
  )
}
